'use client'

import {
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { ReactNode, RefObject } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  IconArrowLeft,
  IconAdjustmentsHorizontal,
  IconArrowsVertical,
  IconBookmark,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCopy,
  IconDots,
  IconExternalLink,
  IconMinus,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconRepeat,
  IconVolume,
  IconX
} from '@tabler/icons-react'
import { Badge } from '@/components/ui/badge'
import { LoginDialog } from '@/components/auth/login-dialog'
import { useIsMobile } from '@/hooks/use-mobile'
import { IconBook } from '@tabler/icons-react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { useAudio, type PlayHandle } from '@/hooks/useAudio'
import { useAuth } from '@/hooks/useAuth'
import { Link } from '@/i18n/routing'
import { analytics } from '@/lib/analytics'
import { storyLearningLocator } from '@/lib/learning-state-core'
import { upsertLearningContentState } from '@/lib/learning-state'
import { recordReaderExposure } from '@/lib/reader/exposure'
import {
  ONBOARDING_STATE_CHANGED_EVENT,
  readOnboardingState,
  shouldShowActivationCard,
  type OnboardingState
} from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import {
  getBlockText,
  getPlayableBlocks,
  getTokenText
} from '@/lib/reader/content'
import { getReaderEntry } from '@/lib/reader/glossary'
import {
  createReaderProgress,
  readLocalReaderProgress,
  saveLocalReaderProgress,
  type ReaderProgress
} from '@/lib/reader/progress'
import {
  readSavedSentences,
  SAVED_SENTENCES_CHANGED_EVENT,
  type SavedReaderSentence
} from '@/lib/reader/saved-sentences'
import { getUserStudyDecks } from '@/lib/study'
import type {
  ReaderBlock,
  ReaderContent,
  ReaderGrammarSummaryItem,
  ReaderGrammarUsage,
  ReaderGlossaryEntry,
  ReaderToken
} from '@/types/reader'
import type { StudyDeckWithCounts } from '@/types/study'
import {
  DEFAULT_READER_SETTINGS,
  useReaderSettings
} from './use-reader-settings'
import { ReaderSentenceActions } from './reader-sentence-actions'
import { ReaderSaveSentenceButton } from './reader-save-sentence-button'
import { ReaderStudyTextDialog } from './reader-study-text-dialog'
import { ContentFamiliarityStrip } from './content-familiarity-strip'
import { SaveStudyWordButton } from '@/components/study/save-study-word-button'

type ReaderMediaControls = {
  currentTime?: number
  duration?: number
  isPlaying?: boolean
  onPlayPause?: () => void
  onSeek?: (seconds: number) => void
  syncOffset?: number
  onSyncOffsetChange?: (seconds: number) => void
  syncScale?: number
  onSyncScaleChange?: (scale: number) => void
}

type ReaderShellProps = {
  content: ReaderContent
  onBack?: () => void
  backHref?: string
  media?: ReaderMediaControls
  mediaSlot?: ReactNode
  sideSlot?: ReactNode
  scrollMode?: 'contained' | 'page'
  initialProgress?: ReaderProgress | null
  onProgressChange?: (progress: ReaderProgress) => void
  className?: string
}

type SelectedToken = {
  token: ReaderToken
  block: ReaderBlock
}

type ContinuousPlaybackSession = {
  cancelled: boolean
  handle: PlayHandle | null
}

function useDesktopReaderChrome() {
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)')
    const update = () => setIsDesktop(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return isDesktop
}

function findActiveLyricIndex(
  blocks: ReaderBlock[],
  currentTime: number | undefined,
  offset: number,
  scale: number
) {
  if (typeof currentTime !== 'number') return -1

  const adjustedTime = currentTime * scale + offset
  return blocks.findIndex((block, index) => {
    if (typeof block.startTime !== 'number') return false
    const nextStart = blocks[index + 1]?.startTime
    const end = block.endTime ?? nextStart ?? Number.POSITIVE_INFINITY
    return adjustedTime >= block.startTime && adjustedTime < end
  })
}

function formatTime(seconds: number | undefined) {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '0:00'
  const minutes = Math.floor(seconds / 60)
  const rest = Math.floor(seconds % 60)
  return `${minutes}:${String(rest).padStart(2, '0')}`
}

function getBlockDisplayText(
  block: Pick<ReaderBlock, 'originalText' | 'tokens'>,
  script: 'simplified' | 'traditional'
) {
  const tokenText = block.tokens
    .map((token) => getTokenText(token, script))
    .join('')
  return tokenText || getBlockText(block)
}

function getCurrentReaderHref() {
  if (typeof window === 'undefined') return null
  return `${window.location.pathname}${window.location.search}`
}

function getSentenceAnchorId(sentenceId: string) {
  return `sentence-${sentenceId}`
}

export function ReaderShell({
  content,
  onBack,
  backHref = '/read',
  media,
  mediaSlot,
  sideSlot,
  scrollMode = 'contained',
  initialProgress,
  onProgressChange,
  className
}: ReaderShellProps) {
  const { settings, setSettings } = useReaderSettings()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [selected, setSelected] = useState<SelectedToken | null>(null)
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({})
  const [activeIndex, setActiveIndex] = useState(0)
  const [autoScrollLyrics, setAutoScrollLyrics] = useState(true)
  const [finishingStory, setFinishingStory] = useState(false)
  const [storyFinished, setStoryFinished] = useState(false)
  const [savedSentences, setSavedSentences] = useState<SavedReaderSentence[]>(
    []
  )
  const isDesktop = useDesktopReaderChrome()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const programmaticScrollUntilRef = useRef(0)
  const progressTimeoutRef = useRef<number | null>(null)
  const scrollFrameRef = useRef<number | null>(null)
  const lastProgressKeyRef = useRef('')
  const exposureKeyRef = useRef('')
  const restoredContentIdRef = useRef<string | null>(null)
  const continuousPlaybackRef = useRef<ContinuousPlaybackSession | null>(null)
  const blockRefs = useMemo(
    () => content.blocks.map(() => createRef<HTMLElement>()),
    [content.blocks]
  )
  const [continuousPlaying, setContinuousPlaying] = useState(false)
  const [syncOffset, setSyncOffset] = useState(
    content.metadata?.lyricsOffset ?? media?.syncOffset ?? 0
  )
  const [syncScale, setSyncScale] = useState(
    content.metadata?.lyricsSyncScale ?? media?.syncScale ?? 1
  )
  const {
    play: playAudio,
    stop: stopAudio,
    isPlaying: audioIsPlaying
  } = useAudio(settings.script)
  const playableBlocks = getPlayableBlocks(content)
  const lyricsMode = content.sourceType === 'lyrics'
  const pageScroll = scrollMode === 'page'
  const onboardingActive = Boolean(
    content.sourceType === 'story' &&
      content.metadata?.slug === onboardingState.recommendedStorySlug &&
      shouldShowActivationCard(onboardingState)
  )
  const mediaActiveIndex = lyricsMode
    ? findActiveLyricIndex(
        content.blocks,
        media?.currentTime,
        syncOffset,
        syncScale
      )
    : -1
  const visibleActiveIndex =
    mediaActiveIndex >= 0 ? mediaActiveIndex : activeIndex
  const storySlug =
    content.sourceType === 'story' ? (content.metadata?.slug ?? null) : null
  const effectiveSettings =
    lyricsMode &&
    isDesktop &&
    settings.textSize === DEFAULT_READER_SETTINGS.textSize
      ? {
          ...settings,
          textSize: 26,
          lineSpacing: Math.max(settings.lineSpacing, 1.85)
        }
      : settings

  const scrollBlockIntoView = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const node = blockRefs[index]?.current
      if (!node) return

      programmaticScrollUntilRef.current = Date.now() + 1500
      node.scrollIntoView({
        behavior,
        block: 'center',
        inline: 'nearest'
      })
    },
    [blockRefs]
  )

  const stopContinuousPlayback = useCallback(() => {
    const session = continuousPlaybackRef.current
    if (session) {
      session.cancelled = true
      session.handle?.cancel()
      continuousPlaybackRef.current = null
    }
    stopAudio()
    setContinuousPlaying(false)
  }, [stopAudio])

  useEffect(() => {
    return () => {
      const session = continuousPlaybackRef.current
      if (session) {
        session.cancelled = true
        session.handle?.cancel()
        continuousPlaybackRef.current = null
      }
      stopAudio()
    }
  }, [stopAudio, content.id])

  useEffect(() => {
    const refresh = () => setSavedSentences(readSavedSentences())
    refresh()
    window.addEventListener(SAVED_SENTENCES_CHANGED_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(SAVED_SENTENCES_CHANGED_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  useEffect(() => {
    const refresh = () => setOnboardingState(readOnboardingState())
    refresh()
    window.addEventListener(ONBOARDING_STATE_CHANGED_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(ONBOARDING_STATE_CHANGED_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const exposureKey = `${user.id}:${content.id}`
    if (exposureKeyRef.current === exposureKey) return
    exposureKeyRef.current = exposureKey

    void recordReaderExposure({
      userId: user.id,
      content,
      storyStatus: content.sourceType === 'story' ? 'studying' : 'seen'
    }).then(() => {
      queryClient.invalidateQueries({
        queryKey: ['content-familiarity', user.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['path-story-readiness', user.id]
      })
    })
  }, [content, queryClient, user?.id])

  useEffect(() => {
    if (restoredContentIdRef.current === content.id || !content.blocks.length) {
      return
    }
    const progress = initialProgress ?? readLocalReaderProgress(content) ?? null
    const nextIndex = progress?.blockIndex ?? 0
    const frame = window.requestAnimationFrame(() => {
      setActiveIndex(nextIndex)
      restoredContentIdRef.current = content.id
      lastProgressKeyRef.current = ''

      if (nextIndex > 0) {
        scrollBlockIntoView(nextIndex, 'auto')
      }
    })

    return () => window.cancelAnimationFrame(frame)
  }, [blockRefs, content, initialProgress, scrollBlockIntoView])

  useEffect(() => {
    if (typeof window === 'undefined' || content.sourceType !== 'story') return
    const sentenceId = window.location.hash.match(/^#sentence-(.+)$/)?.[1]
    if (!sentenceId) return

    const blockIndex = content.blocks.findIndex(
      (block) => block.sourceSentenceId === sentenceId
    )
    if (blockIndex < 0) return

    const frame = window.requestAnimationFrame(() => {
      setActiveIndex(blockIndex)
      scrollBlockIntoView(blockIndex)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [blockRefs, content, scrollBlockIntoView])

  useEffect(() => {
    const progress = createReaderProgress(content, visibleActiveIndex)
    if (!progress) return
    const key = `${progress.blockId}:${progress.blockIndex}`
    if (key === lastProgressKeyRef.current) return
    lastProgressKeyRef.current = key

    if (progressTimeoutRef.current) {
      window.clearTimeout(progressTimeoutRef.current)
    }

    progressTimeoutRef.current = window.setTimeout(() => {
      onProgressChange?.(progress)
      if (content.sourceType === 'story') {
        const href = getCurrentReaderHref()
        if (href) saveLocalReaderProgress(content, href, progress)
        if (user?.id && storySlug) {
          void upsertLearningContentState({
            userId: user.id,
            ...storyLearningLocator(storySlug),
            status: 'studying',
            progress: progress.percent / 100,
            metadata: {
              title: content.title,
              href,
              contentId: content.id,
              blockId: progress.blockId
            }
          })
        }
      }
    }, 900)

    return () => {
      if (progressTimeoutRef.current) {
        window.clearTimeout(progressTimeoutRef.current)
        progressTimeoutRef.current = null
      }
    }
  }, [content, onProgressChange, storySlug, user?.id, visibleActiveIndex])

  const handleReaderScroll = useCallback(() => {
    if (lyricsMode || !scrollContainerRef.current) return
    if (Date.now() < programmaticScrollUntilRef.current) return
    if (scrollFrameRef.current !== null) return

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null
      const containerTop =
        scrollContainerRef.current?.getBoundingClientRect().top ?? 0
      const targetLine = containerTop + 120
      let nextIndex = 0

      for (let index = 0; index < blockRefs.length; index += 1) {
        const node = blockRefs[index]?.current
        if (!node) continue
        if (node.getBoundingClientRect().top <= targetLine) {
          nextIndex = index
        } else {
          break
        }
      }

      setActiveIndex((current) => (current === nextIndex ? current : nextIndex))
    })
  }, [blockRefs, lyricsMode])

  useEffect(() => {
    if (!pageScroll || lyricsMode) return

    window.addEventListener('scroll', handleReaderScroll, { passive: true })
    window.addEventListener('resize', handleReaderScroll)
    handleReaderScroll()
    return () => {
      window.removeEventListener('scroll', handleReaderScroll)
      window.removeEventListener('resize', handleReaderScroll)
    }
  }, [handleReaderScroll, lyricsMode, pageScroll])

  const centerActiveLyric = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      if (!lyricsMode) return
      const node = blockRefs[visibleActiveIndex]?.current
      if (!node) return

      programmaticScrollUntilRef.current = Date.now() + 2500
      node.scrollIntoView({
        behavior,
        block: 'center',
        inline: 'nearest'
      })
    },
    [blockRefs, lyricsMode, visibleActiveIndex]
  )

  useEffect(() => {
    if (!lyricsMode || !autoScrollLyrics) return
    const frame = window.requestAnimationFrame(() => {
      centerActiveLyric()
    })
    const timeout = window.setTimeout(() => {
      centerActiveLyric('auto')
    }, 150)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timeout)
    }
  }, [lyricsMode, autoScrollLyrics, visibleActiveIndex, centerActiveLyric])

  useEffect(() => {
    if (!lyricsMode) return

    const pauseAutoScroll = () => {
      if (Date.now() < programmaticScrollUntilRef.current) return
      setAutoScrollLyrics(false)
    }

    const container = scrollContainerRef.current
    container?.addEventListener('wheel', pauseAutoScroll, { passive: true })
    container?.addEventListener('touchmove', pauseAutoScroll, { passive: true })
    window.addEventListener('keydown', pauseAutoScroll)
    return () => {
      container?.removeEventListener('wheel', pauseAutoScroll)
      container?.removeEventListener('touchmove', pauseAutoScroll)
      window.removeEventListener('keydown', pauseAutoScroll)
    }
  }, [lyricsMode])

  const setOffset = (nextOffset: number) => {
    const rounded = Math.round(nextOffset * 10) / 10
    setSyncOffset(rounded)
    media?.onSyncOffsetChange?.(rounded)
  }

  const setScale = (nextScale: number) => {
    const rounded =
      Math.round(Math.min(1.08, Math.max(0.92, nextScale)) * 1000) / 1000
    setSyncScale(rounded)
    media?.onSyncScaleChange?.(rounded)
  }

  const playBlock = async (
    index: number,
    options: { continuous?: boolean; scroll?: boolean } = {}
  ) => {
    const block = content.blocks[index]
    if (!block) return

    if (!options.continuous) {
      stopContinuousPlayback()
    }

    setActiveIndex(index)
    if (options.scroll) {
      scrollBlockIntoView(index)
    }

    if (lyricsMode && typeof block.startTime === 'number' && media?.onSeek) {
      media.onSeek(Math.max(0, (block.startTime - syncOffset) / syncScale))
      return
    }

    const text = getBlockDisplayText(block, settings.script).trim()
    if (!text) return

    return playAudio(text)
  }

  const startContinuousPlayback = async () => {
    if (lyricsMode) return

    stopContinuousPlayback()

    const session: ContinuousPlaybackSession = {
      cancelled: false,
      handle: null
    }
    continuousPlaybackRef.current = session
    setContinuousPlaying(true)

    for (
      let index = Math.max(0, visibleActiveIndex);
      index < content.blocks.length;
      index += 1
    ) {
      if (session.cancelled || continuousPlaybackRef.current !== session) break

      const handle = await playBlock(index, {
        continuous: true,
        scroll: index !== visibleActiveIndex
      })

      if (!handle) continue

      session.handle = handle
      try {
        await handle.finished
      } catch {
        if (!session.cancelled) break
      } finally {
        if (session.handle === handle) {
          session.handle = null
        }
      }

      if (session.cancelled || continuousPlaybackRef.current !== session) break
      await new Promise((resolve) => window.setTimeout(resolve, 120))
    }

    if (continuousPlaybackRef.current === session) {
      continuousPlaybackRef.current = null
      setContinuousPlaying(false)
    }
  }

  const goToRelativeBlock = (direction: -1 | 1) => {
    const nextIndex = Math.min(
      content.blocks.length - 1,
      Math.max(0, visibleActiveIndex + direction)
    )
    void playBlock(nextIndex)
  }

  const repeatCurrent = () => {
    void playBlock(Math.max(0, visibleActiveIndex))
  }

  const togglePlayback = () => {
    if (lyricsMode && media?.onPlayPause) {
      media.onPlayPause()
      return
    }

    if (continuousPlaying || audioIsPlaying) {
      stopContinuousPlayback()
    } else {
      void startContinuousPlayback()
    }
  }

  const selectToken = (token: ReaderToken, block: ReaderBlock) => {
    const entry = getReaderEntry(content, token.entryId)
    analytics.readerWordTapped(
      token.entryId ?? null,
      content.sourceType,
      Boolean(entry)
    )
    setSelected({ token, block })
  }

  const finishStory = async () => {
    if (!user?.id || !storySlug) return
    setFinishingStory(true)
    try {
      await recordReaderExposure({
        userId: user.id,
        content,
        storyStatus: 'completed'
      })
      const finalProgress = createReaderProgress(
        content,
        Math.max(0, content.blocks.length - 1)
      )
      const href = getCurrentReaderHref()
      if (finalProgress && href)
        saveLocalReaderProgress(content, href, finalProgress)
      analytics.storyCompleted(storySlug, 0)
      queryClient.invalidateQueries({
        queryKey: ['learning-content-states', user.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['content-familiarity', user.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['path-story-readiness', user.id]
      })
      queryClient.invalidateQueries({ queryKey: ['story-recommendations'] })
      setStoryFinished(true)
    } finally {
      setFinishingStory(false)
    }
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={pageScroll ? undefined : handleReaderScroll}
      className={cn(
        'bg-background text-foreground box-border flex w-full max-w-full min-w-0 flex-col overflow-x-hidden',
        pageScroll
          ? 'min-h-[calc(100dvh-var(--product-shell-header-height))]'
          : 'h-dvh overflow-y-auto',
        className
      )}
    >
      <header
        className={cn(
          'border-border/70 bg-background/92 supports-[backdrop-filter]:bg-background/75 z-20 border-b px-3 py-2 backdrop-blur sm:px-5',
          pageScroll ? 'relative' : 'sticky top-0'
        )}
      >
        <div className="mx-auto flex w-full max-w-7xl min-w-0 items-center gap-2">
          {onBack ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              aria-label="Back"
            >
              <IconArrowLeft className="h-5 w-5" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" asChild aria-label="Back">
              <Link href={backHref}>
                <IconArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm leading-tight font-semibold">
              {content.title}
            </h1>
            {content.subtitle && (
              <p className="text-muted-foreground truncate text-xs">
                {content.subtitle}
              </p>
            )}
          </div>
          {content.sourceType !== 'lyrics' &&
            Object.keys(content.glossary).length > 0 && (
              <ReaderStudyTextDialog content={content} />
            )}
          {isDesktop ? (
            <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Reader settings"
                >
                  <span className="text-base font-semibold">Aa</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <ReaderSettingsPanel
                  settings={settings}
                  onChange={setSettings}
                  hasTranslations={content.blocks.some(
                    (block) => block.translation
                  )}
                  sourceScript={content.metadata?.sourceScript}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Reader settings"
              onClick={() => setSettingsOpen(true)}
            >
              <span className="text-base font-semibold">Aa</span>
            </Button>
          )}
          {isDesktop ? (
            <Popover open={infoOpen} onOpenChange={setInfoOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Reader info">
                  <IconDots className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <ReaderInfoPanel
                  content={content}
                  savedSentences={savedSentences}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Reader info"
              onClick={() => setInfoOpen(true)}
            >
              <IconDots className="h-5 w-5" />
            </Button>
          )}
        </div>
      </header>

      <ReaderFocusToolbar
        settings={settings}
        onChange={setSettings}
        hasTranslations={content.blocks.some((block) => block.translation)}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      <main
        className={cn(
          'box-border w-full max-w-full min-w-0 flex-1 overflow-x-hidden px-4 pt-6 sm:px-6 md:pt-8',
          lyricsMode
            ? 'pb-32 md:pb-10'
            : pageScroll
              ? 'pb-[calc(9rem+env(safe-area-inset-bottom))] md:pb-16'
              : 'pb-36'
        )}
      >
        {lyricsMode && mediaSlot ? (
          <div className="mx-auto grid w-full max-w-7xl min-w-0 gap-5 lg:grid-cols-[minmax(280px,390px)_minmax(0,1fr)] xl:grid-cols-[minmax(320px,430px)_minmax(0,1fr)]">
            <aside
              className={cn(
                'min-w-0 lg:sticky lg:self-start',
                pageScroll
                  ? 'lg:top-[calc(var(--product-shell-header-height)+4rem)]'
                  : 'lg:top-20'
              )}
            >
              <div className="mx-auto w-full max-w-[310px] overflow-hidden sm:max-w-sm lg:max-w-none">
                {mediaSlot}
              </div>
            </aside>
            <ReaderArticle
              content={content}
              mediaSlot={mediaSlot}
              visibleActiveIndex={visibleActiveIndex}
              settings={effectiveSettings}
              lyricsMode={lyricsMode}
              blockRefs={blockRefs}
              onboardingActive={onboardingActive}
              storyFinished={storyFinished}
              finishingStory={finishingStory}
              onFinishStory={finishStory}
              onTokenSelect={selectToken}
              onBlockClick={(blockIndex) => void playBlock(blockIndex)}
              onBlockPlay={(blockIndex) => void playBlock(blockIndex)}
            />
          </div>
        ) : (
          <div
            className={cn(
              'mx-auto grid w-full max-w-7xl min-w-0 gap-8',
              sideSlot &&
                'lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]'
            )}
          >
            <ReaderArticle
              content={content}
              mediaSlot={mediaSlot}
              visibleActiveIndex={visibleActiveIndex}
              settings={effectiveSettings}
              lyricsMode={lyricsMode}
              blockRefs={blockRefs}
              onboardingActive={onboardingActive}
              storyFinished={storyFinished}
              finishingStory={finishingStory}
              onFinishStory={finishStory}
              onTokenSelect={selectToken}
              onBlockClick={(blockIndex) => void playBlock(blockIndex)}
              onBlockPlay={(blockIndex) => void playBlock(blockIndex)}
            />
            {sideSlot && (
              <aside className="hidden min-w-0 lg:block">
                <div
                  className={cn(
                    'sticky',
                    pageScroll
                      ? 'top-[calc(var(--product-shell-header-height)+4rem)]'
                      : 'top-20'
                  )}
                >
                  {sideSlot}
                </div>
              </aside>
            )}
          </div>
        )}
      </main>

      <ReaderPlaybackBar
        lyricsMode={lyricsMode}
        pageScroll={pageScroll}
        isPlaying={
          lyricsMode
            ? Boolean(media?.isPlaying)
            : audioIsPlaying || continuousPlaying
        }
        activeIndex={visibleActiveIndex}
        blockCount={content.blocks.length || playableBlocks.length}
        hasExternalMedia={lyricsMode && Boolean(media?.onSeek)}
        currentTime={media?.currentTime}
        duration={media?.duration}
        syncOffset={syncOffset}
        syncScale={syncScale}
        onTogglePlayback={togglePlayback}
        onPrevious={() => goToRelativeBlock(-1)}
        onNext={() => goToRelativeBlock(1)}
        onRepeat={repeatCurrent}
        onNudgeOffset={(amount) => setOffset(syncOffset + amount)}
        onResetOffset={() => setOffset(0)}
        onNudgeScale={(amount) => setScale(syncScale + amount)}
        onResetScale={() => setScale(1)}
      />

      {lyricsMode && !autoScrollLyrics && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="fixed bottom-36 left-1/2 z-30 -translate-x-1/2 rounded-full border shadow-lg md:bottom-28 lg:right-8 lg:left-auto lg:translate-x-0"
          onClick={() => {
            setAutoScrollLyrics(true)
            centerActiveLyric('smooth')
          }}
        >
          <IconArrowsVertical className="h-4 w-4" />
          Sync scroll
        </Button>
      )}

      <ReaderSettingsDrawer
        enabled={!isDesktop}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onChange={setSettings}
        hasTranslations={content.blocks.some((block) => block.translation)}
        sourceScript={content.metadata?.sourceScript}
      />

      <ReaderInfoDrawer
        enabled={!isDesktop}
        open={infoOpen}
        onOpenChange={setInfoOpen}
        content={content}
        savedSentences={savedSentences}
      />

      <ReaderWordLookup
        selected={selected}
        content={content}
        script={settings.script}
        isDesktop={isDesktop}
        onboardingActive={onboardingActive}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      />
    </div>
  )
}

function ReaderArticle({
  content,
  mediaSlot,
  visibleActiveIndex,
  settings,
  lyricsMode,
  blockRefs,
  onboardingActive,
  storyFinished,
  finishingStory,
  onFinishStory,
  onTokenSelect,
  onBlockClick,
  onBlockPlay
}: {
  content: ReaderContent
  mediaSlot?: ReactNode
  visibleActiveIndex: number
  settings: ReturnType<typeof useReaderSettings>['settings']
  lyricsMode: boolean
  blockRefs: Array<RefObject<HTMLElement | null>>
  onboardingActive: boolean
  storyFinished: boolean
  finishingStory: boolean
  onFinishStory: () => void
  onTokenSelect: (token: ReaderToken, block: ReaderBlock) => void
  onBlockClick: (blockIndex: number) => void
  onBlockPlay: (blockIndex: number) => void
}) {
  const { user } = useAuth()
  const { data: decks = [] } = useQuery({
    queryKey: ['reader-sentence-decks', user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      return getUserStudyDecks()
    },
    enabled: !!user?.id
  })

  const storyTitleBlock =
    !lyricsMode &&
    content.sourceType === 'story' &&
    content.blocks[0]?.type === 'section'
      ? content.blocks[0]
      : null
  const storyBodyStartIndex = storyTitleBlock ? 1 : 0

  const renderBlock = (block: ReaderBlock, blockIndex: number) => (
    <ReaderBlockView
      key={block.id}
      ref={blockRefs[blockIndex]}
      block={block}
      content={content}
      active={blockIndex === visibleActiveIndex}
      settings={settings}
      decks={decks}
      lyricsMode={lyricsMode}
      onTokenSelect={(token) => onTokenSelect(token, block)}
      onBlockClick={() => onBlockClick(blockIndex)}
      onBlockPlay={() => onBlockPlay(blockIndex)}
    />
  )

  return (
    <article className="mx-auto w-full max-w-3xl min-w-0 overflow-hidden lg:max-w-[44rem]">
      {!lyricsMode && mediaSlot ? (
        <div className="mb-5 flex min-w-0 justify-center">{mediaSlot}</div>
      ) : null}
      {storyTitleBlock ? (
        <div className="mb-5 min-w-0">{renderBlock(storyTitleBlock, 0)}</div>
      ) : null}
      {onboardingActive ? <ReaderOnboardingHint /> : null}
      {!lyricsMode ? <ContentFamiliarityStrip content={content} /> : null}
      {!lyricsMode && content.grammarSummary?.length ? (
        <GrammarSummaryStrip items={content.grammarSummary} />
      ) : null}
      <div
        className={cn(
          'min-w-0 space-y-5',
          lyricsMode &&
            'space-y-1 text-center before:block before:h-[42vh] after:block after:h-[48vh]'
        )}
      >
        {content.blocks
          .slice(storyBodyStartIndex)
          .map((block, bodyIndex) =>
            renderBlock(block, bodyIndex + storyBodyStartIndex)
          )}
      </div>
      {!lyricsMode && content.sourceType === 'story' && (
        <StoryFinishPanel
          signedIn={Boolean(user?.id)}
          finished={storyFinished}
          finishing={finishingStory}
          onFinish={onFinishStory}
        />
      )}
    </article>
  )
}

function StoryFinishPanel({
  signedIn,
  finished,
  finishing,
  onFinish
}: {
  signedIn: boolean
  finished: boolean
  finishing: boolean
  onFinish: () => void
}) {
  return (
    <section className="bg-card mt-7 rounded-xl border p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="bg-muted text-muted-foreground flex h-9 w-9 items-center justify-center rounded-lg">
              <IconCircleCheck className="h-5 w-5" />
            </span>
            <h2 className="font-semibold">
              {finished ? 'Story finished' : 'Finish this story'}
            </h2>
          </div>
          <p className="text-muted-foreground mt-2 text-sm leading-6">
            {finished
              ? 'This story now counts as read on your path.'
              : 'Mark it read when you are done so your path and recommendations can move forward.'}
          </p>
        </div>
        {signedIn ? (
          <Button onClick={onFinish} disabled={finished || finishing}>
            {finished ? 'Done' : finishing ? 'Saving' : 'Finish story'}
            <IconCircleCheck className="h-4 w-4" />
          </Button>
        ) : (
          <LoginDialog>
            <Button>
              Sign in to finish
              <IconCircleCheck className="h-4 w-4" />
            </Button>
          </LoginDialog>
        )}
      </div>
    </section>
  )
}

function ReaderOnboardingHint() {
  return (
    <div className="bg-muted/45 mb-4 rounded-xl border px-4 py-3 text-sm">
      <p className="font-semibold">Your first reading loop</p>
      <p className="text-muted-foreground mt-1 leading-6">
        Tap a word while you read, then save one useful word or sentence.
      </p>
    </div>
  )
}

function ReaderInfoPanel({
  content,
  savedSentences
}: {
  content: ReaderContent
  savedSentences: SavedReaderSentence[]
}) {
  const stats = {
    lines: content.blocks.length,
    glossary: Object.keys(content.glossary).length
  }
  const currentSavedSentences = savedSentences.filter(
    (sentence) => sentence.contentId === content.id
  )
  const scriptLabel =
    content.metadata?.sourceScript === 'traditional'
      ? 'Traditional'
      : content.metadata?.sourceScript === 'simplified'
        ? 'Simplified'
        : content.metadata?.sourceScript === 'mixed'
          ? 'Mixed'
          : undefined

  return (
    <div className="space-y-4 p-4 text-sm">
      <div>
        <h2 className="text-sm font-semibold">{content.title}</h2>
        <p className="text-muted-foreground mt-1 text-xs">
          {content.subtitle ||
            content.metadata?.fileName ||
            content.metadata?.artist ||
            'Reader item'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-muted/60 rounded-lg p-3">
          <div className="text-muted-foreground text-xs">Blocks</div>
          <div className="text-lg font-semibold">{stats.lines}</div>
        </div>
        <div className="bg-muted/60 rounded-lg p-3">
          <div className="text-muted-foreground text-xs">Glossary</div>
          <div className="text-lg font-semibold">{stats.glossary}</div>
        </div>
      </div>
      {scriptLabel && (
        <p className="text-muted-foreground text-xs">
          Original writing system: {scriptLabel}
        </p>
      )}
      {content.metadata?.attribution && (
        <p className="text-muted-foreground">{content.metadata.attribution}</p>
      )}
      {content.metadata?.providerUrl && (
        <Button variant="outline" size="sm" asChild>
          <a
            href={content.metadata.providerUrl}
            target="_blank"
            rel="noreferrer"
          >
            <IconExternalLink className="h-4 w-4" />
            Source
          </a>
        </Button>
      )}
      <div className="border-t pt-4">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Saved sentences
        </h3>
        {currentSavedSentences.length ? (
          <div className="mt-2 space-y-2">
            {currentSavedSentences.slice(0, 5).map((sentence) => (
              <div key={sentence.id} className="bg-muted/50 rounded-lg p-2">
                <p className="line-clamp-2 text-sm">{sentence.text}</p>
                {sentence.translation && (
                  <p className="text-muted-foreground mt-1 line-clamp-1 text-xs">
                    {sentence.translation}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground mt-2 text-xs leading-5">
            Use Save sentence to add useful lines to Sentence Mining.
          </p>
        )}
      </div>
    </div>
  )
}

function ReaderInfoDrawer({
  enabled,
  open,
  onOpenChange,
  content,
  savedSentences
}: {
  enabled: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  content: ReaderContent
  savedSentences: SavedReaderSentence[]
}) {
  if (!enabled) return null

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-w-lg rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
        <DrawerHeader className="sr-only">
          <DrawerTitle>{content.title}</DrawerTitle>
          <DrawerDescription>Reader details</DrawerDescription>
        </DrawerHeader>
        <ReaderInfoPanel content={content} savedSentences={savedSentences} />
      </DrawerContent>
    </Drawer>
  )
}

function ReaderFocusToolbar({
  settings,
  onChange,
  hasTranslations,
  onOpenSettings
}: {
  settings: ReturnType<typeof useReaderSettings>['settings']
  onChange: (
    settings: Partial<ReturnType<typeof useReaderSettings>['settings']>
  ) => void
  hasTranslations: boolean
  onOpenSettings: () => void
}) {
  const canShrink = settings.textSize > 18
  const canGrow = settings.textSize < 30

  return (
    <div className="border-border/70 bg-background/92 supports-[backdrop-filter]:bg-background/75 sticky top-[49px] z-10 border-b px-3 py-2 backdrop-blur sm:px-5">
      <div className="mx-auto flex w-full max-w-7xl min-w-0 items-center gap-2 overflow-x-auto">
        <div className="bg-muted/60 inline-flex shrink-0 items-center rounded-full p-1">
          {(
            [
              ['simplified', '简'],
              ['traditional', '繁']
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ script: value })}
              className={cn(
                'min-h-8 rounded-full px-3 text-sm font-semibold transition-colors',
                settings.script === value
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-pressed={settings.script === value}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="bg-muted/60 inline-flex shrink-0 items-center gap-1 rounded-full p-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() =>
              onChange({ textSize: Math.max(18, settings.textSize - 1) })
            }
            disabled={!canShrink}
            aria-label="Smaller text"
          >
            <IconMinus className="h-4 w-4" />
          </Button>
          <span className="min-w-12 text-center text-xs font-semibold">
            {settings.textSize}px
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() =>
              onChange({ textSize: Math.min(30, settings.textSize + 1) })
            }
            disabled={!canGrow}
            aria-label="Larger text"
          >
            <IconPlus className="h-4 w-4" />
          </Button>
        </div>

        {hasTranslations && (
          <label className="bg-muted/60 inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full px-3 text-sm font-medium">
            Translation
            <Switch
              checked={settings.showTranslation}
              onCheckedChange={(showTranslation) =>
                onChange({ showTranslation })
              }
            />
          </label>
        )}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto shrink-0 rounded-full"
          onClick={onOpenSettings}
        >
          <IconAdjustmentsHorizontal className="h-4 w-4" />
          More
        </Button>
      </div>
    </div>
  )
}

function readerGrammarLevel(item: ReaderGrammarSummaryItem) {
  return [item.hsk?.toUpperCase(), item.tocfl?.replace('tocfl-', 'TOCFL ')]
    .filter(Boolean)
    .join(' · ')
}

function GrammarSummaryChip({ item }: { item: ReaderGrammarSummaryItem }) {
  const label = item.shortTitle || item.form || item.title || item.conceptId
  const level = readerGrammarLevel(item) || item.difficulty
  const countLabel = item.count === 1 ? '1 sentence' : `${item.count} sentences`
  const secondary = [item.title !== label ? item.title : '', level]
    .filter(Boolean)
    .join(' · ')
  const stateLabel = item.pageHref
    ? countLabel
    : item.unavailableLabel || 'Lesson coming later'
  const content = (
    <>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{label}</span>
        {secondary && (
          <span className="text-muted-foreground mt-0.5 block truncate text-xs">
            {secondary}
          </span>
        )}
      </span>
      <span className="text-muted-foreground shrink-0 text-[11px]">
        {stateLabel}
      </span>
    </>
  )

  if (item.pageHref) {
    return (
      <Link
        href={item.pageHref}
        className="group bg-card hover:bg-muted/50 focus-visible:ring-ring/40 inline-flex min-h-9 max-w-full min-w-0 items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left transition focus-visible:ring-2 focus-visible:outline-none sm:min-h-10 sm:px-3 sm:py-2"
      >
        {content}
      </Link>
    )
  }

  return (
    <span className="bg-muted/35 text-muted-foreground inline-flex min-h-9 max-w-full min-w-0 items-center justify-between gap-2 rounded-md border border-dashed px-2.5 py-1.5 text-left sm:min-h-10 sm:px-3 sm:py-2">
      {content}
    </span>
  )
}

function GrammarSummaryStrip({ items }: { items: ReaderGrammarSummaryItem[] }) {
  const visibleItems = items.slice(0, 4)
  const hiddenItems = items.slice(4)

  return (
    <section className="border-border/70 mb-5 space-y-3 border-b pb-5 sm:mb-7">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Grammar in this story</h2>
          <p className="text-muted-foreground mt-1 hidden text-xs sm:block">
            Concepts you will see while reading.
          </p>
        </div>
        <Badge variant="neutral">{items.length} concepts</Badge>
      </div>
      <div className="grid gap-1.5 sm:grid-cols-2 sm:gap-2">
        {visibleItems.map((item) => (
          <GrammarSummaryChip key={item.conceptId} item={item} />
        ))}
      </div>
      {hiddenItems.length > 0 && (
        <details>
          <summary className="text-muted-foreground hover:text-foreground cursor-pointer list-none text-sm font-medium">
            Show {hiddenItems.length} more
          </summary>
          <div className="mt-2 grid gap-1.5 sm:grid-cols-2 sm:gap-2">
            {hiddenItems.map((item) => (
              <GrammarSummaryChip key={item.conceptId} item={item} />
            ))}
          </div>
        </details>
      )}
    </section>
  )
}

function ReaderSaveWordButton({
  entry,
  content,
  sourceType
}: {
  entry: ReaderGlossaryEntry
  content: ReaderContent
  sourceType: ReaderContent['sourceType']
}) {
  return (
    <SaveStudyWordButton
      entry={entry}
      source={{
        sourceType,
        source_type: sourceType,
        contentId: content.id,
        content_id: content.id,
        title: content.title,
        readerHref: getCurrentReaderHref(),
        reader_href: getCurrentReaderHref(),
        storySlug: content.metadata?.slug ?? null,
        story_slug: content.metadata?.slug ?? null
      }}
      className="w-full"
      variant="default"
      savedLabel="Saved word"
      analyticsSource={sourceType}
    />
  )
}

type ReaderBlockViewProps = {
  block: ReaderBlock
  content: ReaderContent
  active: boolean
  settings: ReturnType<typeof useReaderSettings>['settings']
  decks: StudyDeckWithCounts[]
  lyricsMode: boolean
  onTokenSelect: (token: ReaderToken) => void
  onBlockClick: () => void
  onBlockPlay: () => void
}

const ReaderBlockView = forwardRef<HTMLElement, ReaderBlockViewProps>(
  function ReaderBlockView(
    {
      block,
      content,
      active,
      settings,
      decks,
      lyricsMode,
      onTokenSelect,
      onBlockClick,
      onBlockPlay
    },
    ref
  ) {
    const sentenceText = getBlockDisplayText(block, settings.script)
    const titleBlock =
      content.sourceType === 'story' && block.type === 'section'

    return (
      <section
        ref={ref}
        id={
          block.sourceSentenceId
            ? getSentenceAnchorId(block.sourceSentenceId)
            : undefined
        }
        className={cn(
          'group relative min-w-0 overflow-hidden rounded-lg px-1.5 py-1.5 transition-colors',
          titleBlock && 'mb-4 border-b pb-5 sm:mb-6 sm:pb-6',
          active && !lyricsMode && 'bg-muted/45 ring-border/80 ring-1',
          active && titleBlock && 'bg-transparent ring-0',
          lyricsMode &&
            'mx-auto w-full max-w-2xl py-2 opacity-55 transition-all duration-200',
          lyricsMode && active && 'scale-[1.02] opacity-100'
        )}
        aria-current={active ? 'true' : undefined}
      >
        <ReaderSentenceActions
          block={block}
          content={content}
          decks={decks}
          lyricsMode={lyricsMode}
          sentenceText={sentenceText}
          onPlay={onBlockPlay}
        />

        <div
          role="button"
          tabIndex={0}
          className="focus-visible:ring-ring/40 w-full min-w-0 cursor-pointer text-left focus-visible:ring-2 focus-visible:outline-none"
          onClick={onBlockClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onBlockClick()
            }
          }}
        >
          <span
            className={cn(
              'flex w-full max-w-full min-w-0 flex-wrap items-end gap-x-1.5 gap-y-2 align-baseline',
              titleBlock && 'block',
              lyricsMode && 'justify-center'
            )}
            style={{
              fontSize: titleBlock
                ? Math.max(settings.textSize + 8, 34)
                : settings.textSize,
              lineHeight: titleBlock ? 1.18 : settings.lineSpacing
            }}
          >
            {block.tokens.map((token, tokenIndex) => (
              <ReaderTokenView
                key={`${block.id}-${tokenIndex}`}
                token={token}
                settings={settings}
                prominent={titleBlock}
                onSelect={() => onTokenSelect(token)}
              />
            ))}
          </span>
        </div>

        {settings.showTranslation && block.translation && (
          <p
            className={cn(
              'text-muted-foreground mt-2 [overflow-wrap:anywhere] break-words',
              titleBlock ? 'text-base leading-7' : 'text-sm leading-6'
            )}
          >
            {block.translation}
          </p>
        )}

        {block.grammarUsages?.length ? (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {block.grammarUsages.map((usage) => {
              if (!usage.display) return null

              return <GrammarBadge key={usage.id} usage={usage} block={block} />
            })}
          </div>
        ) : block.grammar?.length ? (
          <div className="text-muted-foreground mt-2 space-y-1 text-sm [overflow-wrap:anywhere] break-words">
            {block.grammar.map((note, index) => (
              <p key={`${block.id}-grammar-${index}`}>
                <span className="text-foreground font-medium">
                  {note.point}
                </span>
                {note.structure ? `: ${note.structure}. ` : ': '}
                {note.explanation}
              </p>
            ))}
          </div>
        ) : null}

        {content.sourceType === 'lyrics' &&
          active &&
          block.startTime !== undefined && (
            <p className="text-muted-foreground mt-1 text-center text-xs">
              {formatTime(block.startTime)}
            </p>
          )}
      </section>
    )
  }
)

function ReaderTokenView({
  token,
  settings,
  prominent = false,
  onSelect
}: {
  token: ReaderToken
  settings: ReturnType<typeof useReaderSettings>['settings']
  prominent?: boolean
  onSelect: () => void
}) {
  const display = getTokenText(token, settings.script)
  const tappable = token.kind === 'word' || token.kind === 'unknown'

  if (!tappable) {
    return (
      <span
        className={cn(
          'max-w-full shrink-0 [overflow-wrap:anywhere] break-words whitespace-pre-wrap',
          prominent && 'text-foreground font-semibold tracking-tight'
        )}
      >
        {display}
      </span>
    )
  }

  if (prominent) {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onSelect()
        }}
        className="hover:bg-muted focus-visible:ring-ring/40 text-foreground max-w-full rounded-md py-1 text-left font-semibold tracking-tight [overflow-wrap:anywhere] break-words transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {display}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onSelect()
      }}
      className={cn(
        'inline-flex min-h-11 max-w-full min-w-8 shrink-0 flex-col items-center justify-end rounded-sm px-0.5 py-0.5 text-center [overflow-wrap:anywhere] break-all transition-colors',
        'hover:bg-muted focus-visible:ring-ring/40 focus-visible:ring-2 focus-visible:outline-none',
        token.entryId && 'text-foreground',
        !token.entryId && 'text-muted-foreground'
      )}
    >
      {settings.showZhuyin && token.zhuyin && (
        <span className="text-muted-foreground max-w-full text-[0.55em] leading-tight break-all">
          {token.zhuyin}
        </span>
      )}
      {settings.showPinyin && token.pinyin && (
        <span className="text-muted-foreground max-w-full text-[0.55em] leading-tight break-all">
          {token.pinyin}
        </span>
      )}
      <span className="max-w-full break-all">{display}</span>
    </button>
  )
}

function ReaderSettingsDrawer({
  enabled,
  open,
  onOpenChange,
  settings,
  onChange,
  hasTranslations,
  sourceScript
}: {
  enabled: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  settings: ReturnType<typeof useReaderSettings>['settings']
  onChange: (
    settings: Partial<ReturnType<typeof useReaderSettings>['settings']>
  ) => void
  hasTranslations: boolean
  sourceScript?: ReaderContent['metadata'] extends infer M
    ? M extends { sourceScript?: infer S }
      ? S
      : never
    : never
}) {
  if (!enabled) return null

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-w-lg rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
        <DrawerHeader>
          <DrawerTitle>Reader settings</DrawerTitle>
          <DrawerDescription>
            Make the text comfortable for this session.
          </DrawerDescription>
        </DrawerHeader>
        <ReaderSettingsPanel
          settings={settings}
          onChange={onChange}
          hasTranslations={hasTranslations}
          sourceScript={sourceScript}
          className="px-4 pb-5"
        />
      </DrawerContent>
    </Drawer>
  )
}

function ReaderSettingsPanel({
  settings,
  onChange,
  hasTranslations,
  sourceScript,
  className
}: {
  settings: ReturnType<typeof useReaderSettings>['settings']
  onChange: (
    settings: Partial<ReturnType<typeof useReaderSettings>['settings']>
  ) => void
  hasTranslations: boolean
  sourceScript?: ReaderContent['metadata'] extends infer M
    ? M extends { sourceScript?: infer S }
      ? S
      : never
    : never
  className?: string
}) {
  const sourceLabel =
    sourceScript === 'traditional'
      ? 'Traditional source'
      : sourceScript === 'simplified'
        ? 'Simplified source'
        : sourceScript === 'mixed'
          ? 'Mixed source'
          : 'Source unknown'

  return (
    <div className={cn('space-y-5 p-4', className)}>
      <SettingRange
        label="Text size"
        value={settings.textSize}
        min={18}
        max={30}
        step={1}
        onChange={(textSize) => onChange({ textSize })}
      />
      <SettingRange
        label="Line spacing"
        value={settings.lineSpacing}
        min={1.35}
        max={2.2}
        step={0.05}
        onChange={(lineSpacing) => onChange({ lineSpacing })}
      />
      <div className="space-y-2">
        <SegmentedControl
          label="Characters"
          value={settings.script}
          options={[
            { label: '简', value: 'simplified' },
            { label: '繁', value: 'traditional' }
          ]}
          onChange={(script) => onChange({ script })}
        />
        <p className="text-muted-foreground text-xs leading-5">
          {sourceLabel}. Display conversion uses matched dictionary entries;
          unknown characters stay as written.
        </p>
      </div>
      <ToggleRow
        label="Pinyin"
        checked={settings.showPinyin}
        onCheckedChange={(showPinyin) => onChange({ showPinyin })}
      />
      <ToggleRow
        label="Zhuyin"
        checked={settings.showZhuyin}
        onCheckedChange={(showZhuyin) => onChange({ showZhuyin })}
      />
      {hasTranslations && (
        <ToggleRow
          label="Translations"
          checked={settings.showTranslation}
          onCheckedChange={(showTranslation) => onChange({ showTranslation })}
        />
      )}
    </div>
  )
}

function SettingRange({
  label,
  value,
  min,
  max,
  step,
  onChange
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
}) {
  return (
    <label className="grid gap-2">
      <span className="flex items-center justify-between text-sm font-medium">
        <span>{label}</span>
        <span className="text-muted-foreground">
          {value.toFixed(step < 1 ? 2 : 0)}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="accent-primary"
      />
    </label>
  )
}

function ToggleRow({
  label,
  checked,
  onCheckedChange
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string
  value: T
  options: Array<{ label: string; value: T }>
  onChange: (value: T) => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      <div
        className="bg-muted grid gap-1 rounded-lg p-1"
        style={{
          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`
        }}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'min-h-9 rounded-md px-2 text-sm font-medium transition-colors',
              value === option.value
                ? 'bg-background text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ReaderPlaybackBar({
  lyricsMode,
  pageScroll,
  isPlaying,
  activeIndex,
  blockCount,
  currentTime,
  duration,
  hasExternalMedia,
  syncOffset,
  syncScale,
  onTogglePlayback,
  onPrevious,
  onNext,
  onRepeat,
  onNudgeOffset,
  onResetOffset,
  onNudgeScale,
  onResetScale
}: {
  lyricsMode: boolean
  pageScroll: boolean
  isPlaying: boolean
  activeIndex: number
  blockCount: number
  currentTime?: number
  duration?: number
  hasExternalMedia: boolean
  syncOffset: number
  syncScale: number
  onTogglePlayback: () => void
  onPrevious: () => void
  onNext: () => void
  onRepeat: () => void
  onNudgeOffset: (amount: number) => void
  onResetOffset: () => void
  onNudgeScale: (amount: number) => void
  onResetScale: () => void
}) {
  const [advancedSyncOpen, setAdvancedSyncOpen] = useState(false)
  const hasDriftAdjustment = Math.abs(syncScale - 1) > 0.0005
  const progressPercent =
    blockCount > 0
      ? Math.min(100, Math.max(0, ((activeIndex + 1) / blockCount) * 100))
      : 0

  return (
    <div
      className={cn(
        'fixed z-50',
        lyricsMode
          ? 'inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom))] md:bottom-5 lg:inset-x-auto lg:left-6 lg:w-[min(430px,calc(40vw-2rem))]'
          : pageScroll
            ? 'inset-x-3 bottom-[calc(6.75rem+env(safe-area-inset-bottom))] md:bottom-5'
            : 'inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom))] md:bottom-5'
      )}
    >
      <div
        className={cn(
          'border-border/80 bg-background/95 mx-auto flex w-full max-w-lg flex-col gap-2 rounded-2xl border p-2 shadow-2xl shadow-black/10 backdrop-blur',
          lyricsMode ? 'max-w-xl' : 'max-w-md'
        )}
      >
        <div className="flex min-h-11 items-center justify-between gap-3">
          <div className="min-w-0 pl-1">
            <div className="text-foreground text-xs font-semibold">
              {lyricsMode && hasExternalMedia ? 'Song player' : 'Reader audio'}
            </div>
            <div className="text-muted-foreground mt-0.5 truncate text-xs">
              {lyricsMode && hasExternalMedia
                ? `${formatTime(currentTime)} / ${formatTime(duration)}`
                : `Line ${Math.max(1, activeIndex + 1)} of ${Math.max(1, blockCount)}`}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onPrevious}
              aria-label="Previous line"
            >
              <IconChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={onTogglePlayback}
              aria-label="Play or pause"
            >
              {isPlaying ? (
                <IconPlayerPause className="h-5 w-5" />
              ) : (
                <IconPlayerPlay className="h-5 w-5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRepeat}
              aria-label="Repeat line"
            >
              <IconRepeat className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onNext}
              aria-label="Next line"
            >
              <IconChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {!lyricsMode && (
          <div className="bg-muted h-1 overflow-hidden rounded-full">
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-300 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
        {lyricsMode && hasExternalMedia && (
          <div className="bg-muted/50 grid gap-1.5 rounded-xl px-2 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-xs font-medium">
                Offset {syncOffset > 0 ? '+' : ''}
                {syncOffset.toFixed(1)}s
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onNudgeOffset(-0.5)}
                  aria-label="Lyrics earlier"
                >
                  <IconMinus className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={onResetOffset}>
                  Reset
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onNudgeOffset(0.5)}
                  aria-label="Lyrics later"
                >
                  <IconPlus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground h-8 justify-start px-1.5 text-xs"
              onClick={() => setAdvancedSyncOpen((open) => !open)}
            >
              <IconAdjustmentsHorizontal className="h-4 w-4" />
              {hasDriftAdjustment
                ? `Fine tune ${(syncScale * 100).toFixed(1)}%`
                : 'Fine tune'}
            </Button>
            {advancedSyncOpen && (
              <div className="flex items-center justify-between gap-2 border-t pt-1.5">
                <span className="text-muted-foreground text-xs font-medium">
                  Drift {(syncScale * 100).toFixed(1)}%
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onNudgeScale(-0.0025)}
                    aria-label="Lyrics drift slower"
                  >
                    <IconMinus className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={onResetScale}>
                    Reset
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onNudgeScale(0.0025)}
                    aria-label="Lyrics drift faster"
                  >
                    <IconPlus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ReaderWordLookup({
  selected,
  content,
  script,
  isDesktop,
  onboardingActive,
  onOpenChange
}: {
  selected: SelectedToken | null
  content: ReaderContent
  script: 'simplified' | 'traditional'
  isDesktop: boolean
  onboardingActive: boolean
  onOpenChange: (open: boolean) => void
}) {
  const audio = useAudio(script)
  const entry = getReaderEntry(content, selected?.token.entryId)
  const display = selected ? getTokenText(selected.token, script) : ''
  const definitions = entry?.definitions?.length
    ? entry.definitions
    : ['No local glossary entry for this token.']

  const copyText = async () => {
    if (!display || typeof navigator === 'undefined') return
    await navigator.clipboard?.writeText(display)
  }

  if (isDesktop && selected) {
    return (
      <aside className="bg-popover text-popover-foreground fixed top-20 right-5 z-40 w-[min(380px,calc(100vw-2.5rem))] rounded-xl border p-4 shadow-xl">
        <WordLookupContent
          entry={entry}
          selected={selected}
          content={content}
          display={display}
          definitions={definitions}
          script={script}
          onboardingActive={onboardingActive}
          onCopy={() => void copyText()}
          onPlay={() => void audio.play(display)}
          onClose={() => onOpenChange(false)}
        />
      </aside>
    )
  }

  return (
    <Drawer open={Boolean(selected)} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-w-lg rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
        {selected && (
          <WordLookupContent
            entry={entry}
            selected={selected}
            content={content}
            display={display}
            definitions={definitions}
            script={script}
            onboardingActive={onboardingActive}
            onCopy={() => void copyText()}
            onPlay={() => void audio.play(display)}
          />
        )}
      </DrawerContent>
    </Drawer>
  )
}

function WordLookupContent({
  entry,
  selected,
  content,
  display,
  definitions,
  script,
  onboardingActive,
  onCopy,
  onPlay,
  onClose
}: {
  entry: ReturnType<typeof getReaderEntry>
  selected: SelectedToken
  content: ReaderContent
  display: string
  definitions: string[]
  script: 'simplified' | 'traditional'
  onboardingActive: boolean
  onCopy: () => void
  onPlay: () => void
  onClose?: () => void
}) {
  const word = entry
    ? getTokenText(
        {
          surface: entry.simplified,
          simplified: entry.simplified,
          traditional: entry.traditional,
          kind: 'word'
        },
        script
      )
    : display
  const sentenceText = getBlockDisplayText(selected.block, script)
  const shortDefinitions = definitions.slice(0, 3)

  return (
    <>
      <div className={cn('text-left', onClose ? 'pb-4' : 'px-4 pt-4 pb-2')}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {onClose ? (
              <h2 className="text-3xl leading-tight font-semibold break-words">
                {word}
              </h2>
            ) : (
              <>
                <DrawerTitle className="text-3xl break-words">
                  {word}
                </DrawerTitle>
                <DrawerDescription className="sr-only">
                  Instant dictionary details for the selected reader word.
                </DrawerDescription>
              </>
            )}
            <p className="text-muted-foreground mt-1 text-sm">
              {[entry?.pinyin, entry?.zhuyin].filter(Boolean).join(' · ') ||
                selected.token.pinyin ||
                'Unknown word'}
            </p>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label="Close word lookup"
            >
              <IconX className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className={cn('space-y-4', onClose ? '' : 'px-4 pb-5')}>
        <ol className="space-y-2">
          {shortDefinitions.map((definition, index) => (
            <li key={`${definition}-${index}`} className="text-sm leading-6">
              {definition}
            </li>
          ))}
        </ol>
        {onboardingActive && (
          <div className="bg-muted/50 rounded-lg px-3 py-2 text-sm">
            <p className="font-semibold">Save one item to finish setup</p>
            <p className="text-muted-foreground mt-1 leading-5">
              A saved word or sentence will appear in your study decks.
            </p>
          </div>
        )}
        <div className="space-y-2">
          {entry?.id ? (
            <ReaderSaveWordButton
              entry={entry}
              content={content}
              sourceType={content.sourceType}
            />
          ) : (
            <Button className="w-full" disabled>
              <IconBookmark className="h-4 w-4" />
              Save word
            </Button>
          )}
          <ReaderSaveSentenceButton
            block={selected.block}
            content={content}
            sentenceText={sentenceText}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" size="sm" onClick={onPlay}>
            <IconVolume className="h-4 w-4" />
            Audio
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link
              href={
                entry?.id
                  ? `/dict?id=${entry.id}&q=${encodeURIComponent(word)}`
                  : `/dict?q=${encodeURIComponent(display)}`
              }
            >
              <IconExternalLink className="h-4 w-4" />
              {entry?.id ? 'Open' : 'Search'}
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={onCopy}>
            <IconCopy className="h-4 w-4" />
            Copy
          </Button>
        </div>
      </div>
    </>
  )
}

interface GrammarBadgeProps {
  usage: ReaderGrammarUsage
  block: ReaderBlock
}

export function GrammarBadge({ usage, block }: GrammarBadgeProps) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()
  const display = usage.display
  if (!display) return null

  const badgeLabel =
    display.shortTitle || display.title || display.form || usage.conceptId
  const title = display.title || badgeLabel
  const pattern = display.pattern || display.form
  const explanation = display.explanation || display.summary

  const trigger = (
    <Badge
      variant="secondary"
      className="hover:bg-secondary/80 shrink-0 cursor-pointer gap-1.5 border px-2.5 py-1 text-xs transition-colors select-none"
      onClick={() => setOpen(true)}
    >
      <IconBook className="text-primary/70 h-3.5 w-3.5" />
      <span className="text-foreground font-semibold">{badgeLabel}</span>
    </Badge>
  )

  const content = (
    <div className="space-y-4 p-4 text-left sm:p-0">
      {pattern && (
        <div className="space-y-1">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Pattern
          </span>
          <p className="bg-muted w-fit rounded border px-3 py-1.5 font-mono text-sm">
            {pattern}
          </p>
        </div>
      )}

      {explanation && (
        <div className="space-y-1.5">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Explanation
          </span>
          <p className="text-foreground text-sm leading-relaxed">
            {explanation}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {display.difficulty && (
          <Badge variant="neutral" className="capitalize">
            {display.difficulty}
          </Badge>
        )}
        {display.hsk && (
          <Badge variant="success" className="uppercase">
            {display.hsk}
          </Badge>
        )}
        {display.tocfl && (
          <Badge variant="neutral" className="uppercase">
            {display.tocfl}
          </Badge>
        )}
      </div>

      {display.pageHref && (
        <Button asChild variant="outline" size="sm" className="w-full">
          <Link href={display.pageHref}>
            <IconExternalLink className="h-4 w-4" />
            Open lesson
          </Link>
        </Button>
      )}

      <div className="bg-muted/30 mt-2 flex flex-col gap-2 rounded-lg border p-3">
        <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
          In This Sentence
        </span>
        <p className="flex flex-wrap items-end gap-x-1 gap-y-1 text-base leading-relaxed [overflow-wrap:anywhere] break-words">
          {block.tokens.map((token, tokenIdx) => {
            const isHighlighted =
              usage.tokenStart !== undefined &&
              usage.tokenEnd !== undefined &&
              tokenIdx >= usage.tokenStart &&
              tokenIdx < usage.tokenEnd

            return (
              <span
                key={tokenIdx}
                className={cn(
                  isHighlighted &&
                    'rounded-xs border-b-2 border-emerald-500 bg-emerald-500/10 px-0.5 font-semibold text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                )}
              >
                {token.surface}
              </span>
            )
          })}
        </p>
        {block.translation && (
          <p className="text-muted-foreground mt-1 border-t pt-2 font-sans text-xs italic">
            {block.translation}
          </p>
        )}
        {usage.contextNote && (
          <p className="border-t pt-2 text-sm leading-relaxed">
            {usage.contextNote}
          </p>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="max-h-[85vh] overflow-y-auto p-4">
            <DrawerHeader className="px-4 text-left">
              <DrawerTitle className="flex items-center gap-2 text-xl font-bold">
                <IconBook className="text-primary h-5 w-5" />
                {title}
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                Grammar concept details and structural explanations.
              </DrawerDescription>
            </DrawerHeader>
            {content}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-[min(30rem,calc(100vw-2rem))] p-4"
      >
        <div className="mb-4 flex items-center gap-2 text-lg font-semibold">
          <IconBook className="text-primary h-5 w-5" />
          {title}
        </div>
        {content}
      </PopoverContent>
    </Popover>
  )
}
